/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import "./css/StaffPage.css";
import {
  getFullSidebarConfig,
  updateSidebarConfig,
  resetSidebarConfig,
} from "../api/sidebarConfigApi";
import { FiChevronDown, FiChevronRight, FiSave, FiRefreshCw } from "react-icons/fi";
import AccessControl from "../components/AccessControl";

// Every role that exists in the system (see models/Staff.js ROLE_LEVELS)
const ALL_ROLES = [
  "SUPERADMIN", "ADMIN", "HR", "NSM", "RSM", "ASM", "TSO",
  "MARKETING", "ACCOUNTS", "STAFF", "AGENT", "BDE",
];

const ADMIN_PANEL_ROLES = ["SUPERADMIN", "ADMIN"];

export default function SidebarConfigPage() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [toast, setToast] = useState({ show: false, message: "" });

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 2500);
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFullSidebarConfig();
      if (res.success) {
        setSections(res.sections || []);
        // Expand all sections by default the first time
        const expanded = {};
        (res.sections || []).forEach((s) => (expanded[s.key] = true));
        setExpandedSections(expanded);
      }
    } catch {
      showToast("Failed to load sidebar config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const toggleSectionExpand = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Mutators — all work on local state; nothing saves until "Save Changes" ──
  const toggleSectionEnabled = (sIdx) => {
    setSections((prev) => {
      const next = [...prev];
      next[sIdx] = { ...next[sIdx], enabled: !next[sIdx].enabled };
      return next;
    });
  };

  const toggleSectionRole = (sIdx, roleName) => {
    setSections((prev) => {
      const next = [...prev];
      const section = { ...next[sIdx] };
      section.roles = section.roles.includes(roleName)
        ? section.roles.filter((r) => r !== roleName)
        : [...section.roles, roleName];
      next[sIdx] = section;
      return next;
    });
  };

  const toggleItemEnabled = (sIdx, iIdx) => {
    setSections((prev) => {
      const next = [...prev];
      const items = [...next[sIdx].items];
      items[iIdx] = { ...items[iIdx], enabled: !items[iIdx].enabled };
      next[sIdx] = { ...next[sIdx], items };
      return next;
    });
  };

  const toggleItemRole = (sIdx, iIdx, roleName) => {
    setSections((prev) => {
      const next = [...prev];
      const items = [...next[sIdx].items];
      const item = { ...items[iIdx] };
      item.roles = item.roles.includes(roleName)
        ? item.roles.filter((r) => r !== roleName)
        : [...item.roles, roleName];
      items[iIdx] = item;
      next[sIdx] = { ...next[sIdx], items };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateSidebarConfig(sections);
      if (res.success) {
        showToast("Sidebar config saved!");
      } else {
        showToast(res.error || "Failed to save");
      }
    } catch {
      showToast("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset the sidebar back to default settings? This discards all customizations.")) return;
    setSaving(true);
    try {
      const res = await resetSidebarConfig();
      if (res.success) {
        setSections(res.sections || []);
        showToast("Reset to defaults");
      } else {
        showToast(res.error || "Failed to reset");
      }
    } catch {
      showToast("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AccessControl allowedRoles={ADMIN_PANEL_ROLES} redirectTo="/" />

      {toast.show && (
        <div className="sf-toast sf-toast-success">
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="sf-page-header section" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
            Sidebar Configuration
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Control which sections/items show up in the sidebar, and for which roles
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn" style={{ background: "#f3f4f6" }} onClick={handleReset} disabled={saving}>
            <FiRefreshCw size={13} style={{ marginRight: 6 }} />
            Reset to Default
          </button>
          <button className="btn btn-success" onClick={handleSave} disabled={saving}>
            <FiSave size={13} style={{ marginRight: 6 }} />
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="sf-loading" style={{ height: "40vh" }}>
          <div
            className="spinner"
            style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }}
          ></div>
          <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading sidebar config…</p>
        </div>
      ) : (
        <div className="section">
          {sections.map((section, sIdx) => (
            <div className="card" key={section.key} style={{ marginBottom: 12, padding: 0, overflow: "hidden" }}>
              {/* Section header row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 18px",
                  background: section.enabled ? "#fff" : "#FAFAFA",
                  cursor: "pointer",
                }}
                onClick={() => toggleSectionExpand(section.key)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {expandedSections[section.key] ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                  <span style={{ fontWeight: 700, fontSize: 14, color: section.enabled ? "#1a1a1a" : "#aaa" }}>
                    {section.label}
                  </span>
                  <span style={{ fontSize: 11, color: "#aaa" }}>({section.items.length} items)</span>
                </div>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={section.enabled}
                    onChange={() => toggleSectionEnabled(sIdx)}
                  />
                  Section Enabled
                </label>
              </div>

              {expandedSections[section.key] && (
                <div style={{ padding: "0 18px 16px" }}>
                  {/* Section-level role visibility */}
                  <div style={{ marginBottom: 14, paddingTop: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 6 }}>
                      Section visible to roles:
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {ALL_ROLES.map((r) => (
                        <label
                          key={r}
                          style={{
                            fontSize: 11,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            background: section.roles.includes(r) ? "#E1F5EE" : "#F3F4F6",
                            color: section.roles.includes(r) ? "#0F6E56" : "#888",
                            padding: "4px 8px",
                            borderRadius: 20,
                            fontWeight: 600,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={section.roles.includes(r)}
                            onChange={() => toggleSectionRole(sIdx, r)}
                            style={{ margin: 0 }}
                          />
                          {r}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Items */}
                  {section.items.map((item, iIdx) => (
                    <div
                      key={item.key}
                      style={{
                        borderTop: "1px solid #f0f0f0",
                        padding: "12px 0",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: item.enabled ? "#1a1a1a" : "#aaa" }}>
                            {item.label}
                          </div>
                          <div style={{ fontSize: 11, color: "#aaa", fontFamily: "monospace" }}>{item.path}</div>
                        </div>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555" }}>
                          <input
                            type="checkbox"
                            checked={item.enabled}
                            onChange={() => toggleItemEnabled(sIdx, iIdx)}
                          />
                          Enabled
                        </label>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {ALL_ROLES.map((r) => (
                          <label
                            key={r}
                            style={{
                              fontSize: 10,
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              background: item.roles.includes(r) ? "#E6F1FB" : "#F3F4F6",
                              color: item.roles.includes(r) ? "#185FA5" : "#aaa",
                              padding: "3px 7px",
                              borderRadius: 20,
                              fontWeight: 600,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.roles.includes(r)}
                              onChange={() => toggleItemRole(sIdx, iIdx, r)}
                              style={{ margin: 0 }}
                            />
                            {r}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}